import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivityPriorityType } from 'src/app/core/services/http/activity-priority/enums/activity-priority-type';
import { Status } from 'src/app/core/services/http/general/enums/status';
import { FormValidators } from 'src/app/shared/form-error/form.validators';

export function getFormGroup(): UntypedFormGroup {
  // tslint:disable-next-line deprecation
  return new UntypedFormBuilder().group(
    {
      ...{
        uuid: [],
        title: [, [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
        description: [, [Validators.minLength(3), Validators.maxLength(10000)]],
        tags: [, [FormValidators.hashTag]],
        activityType: [, Validators.required],
        priority: [, Validators.required],
        defaultRoom: [, [Validators.required]],
        maxAttendees: [
          0,
          [Validators.required, FormValidators.onlyPositiveNumbers, Validators.min(0), Validators.max(10000)]
        ],
        sharedAttendance: [],
        defaultRoster: [],
        status: [Status.Active],
        additionalStudents: [[]],
        excludedStudents: [[]],
        rosterStudents: [[]],
        teacherRequestedStudents: [[]],
        registeredStudentsCount: [0],
        flex: null,
        applyToMasterActivity: [true],
        applyToScheduledActivities: [true],

        // Uses just to have entire object
        activityPriority: [],
        future: [],
        notifyOnChange: false,
        autoAssign: true,
        owner: [],
        defaultActivityStatus: []
      },
      ...getAdditionalControlsForFiltering()
    },
    {
      validator: validateMaxAttendees
    }
  );
}

export function getAdditionalControlsForFiltering(): Record<string, AbstractControl> {
  return new UntypedFormBuilder().group({
    scheduled: [],
    rosterType: []
  }).controls;
}

export function validateMaxAttendees(form: UntypedFormGroup): ValidationErrors {
  const message = {
    validateMaxAttendees: {
      message: {
        text: '',
        interpolateParams: { maxAttendees: 0, allStudents: 0, rosterStudents: 0, additionalStudents: 0 }
      }
    }
  };

  let result: any = message;

  if (!form.controls.priority.value) {
    result = null;
    return result;
  }

  const priorityType = form.controls.activityPriority.value?.type;

  switch (priorityType) {
    // The default value for max attendees should be equal to total number of students from the roster + additional students.
    // This is true for Admin Must Attend, Priority, and Regular Activities
    case ActivityPriorityType.AdminMustAttend: {
      const allStudents =
        form.controls.additionalStudents.value?.length +
        (form.controls.rosterStudents.value?.length - form.controls.excludedStudents.value?.length);

      const maxAttendees = Number(form.controls.maxAttendees.value);

      if (maxAttendees !== allStudents) {
        message.validateMaxAttendees.message.text = 'MAX_ATTENDEES_NOT_MATCH_TO_ROSTER';
        message.validateMaxAttendees.message.interpolateParams.allStudents = allStudents;
        message.validateMaxAttendees.message.interpolateParams.maxAttendees = maxAttendees;
      }
      break;
    }
    case ActivityPriorityType.PriorityRoster: {
      const allStudents =
        form.controls.additionalStudents.value?.length +
        (form.controls.rosterStudents.value?.length - form.controls.excludedStudents.value?.length);

      const maxAttendees = Number(form.controls.maxAttendees.value);

      if (maxAttendees !== allStudents) {
        message.validateMaxAttendees.message.text = 'MAX_ATTENDEES_NOT_MATCH_TO_ROSTER';
        message.validateMaxAttendees.message.interpolateParams.allStudents = allStudents;
        message.validateMaxAttendees.message.interpolateParams.maxAttendees = maxAttendees;
      }
      break;
    }
    case ActivityPriorityType.RegularRoster: {
      const allStudents =
        form.controls.additionalStudents.value?.length +
        (form.controls.rosterStudents.value?.length - form.controls.excludedStudents.value?.length);

      const maxAttendees = Number(form.controls.maxAttendees.value);

      if (maxAttendees !== allStudents) {
        message.validateMaxAttendees.message.text = 'MAX_ATTENDEES_NOT_MATCH_TO_ROSTER';
        message.validateMaxAttendees.message.interpolateParams.allStudents = allStudents;
        message.validateMaxAttendees.message.interpolateParams.maxAttendees = maxAttendees;
      }
      break;
    }

    // For Optional priority activities the user should be able to set the maximum attendees
    // to the number on the roster plus any additional students but not greater than the total number
    case ActivityPriorityType.OptionalRoster: {
      const maxAttendees = Number(form.controls.maxAttendees.value);
      const rosterStudents = form.controls.rosterStudents.value?.length - form.controls.excludedStudents.value?.length;
      const additionalStudents = form.controls.additionalStudents.value?.length;
      const allStudents = rosterStudents + additionalStudents;

      if (Number(form.controls.maxAttendees.value) > allStudents) {
        message.validateMaxAttendees.message.text = 'MAX_ATTENDEES_IS_GREATER_THAN_ROSTER';
        message.validateMaxAttendees.message.interpolateParams.maxAttendees = maxAttendees;
        message.validateMaxAttendees.message.interpolateParams.rosterStudents = rosterStudents;
        message.validateMaxAttendees.message.interpolateParams.additionalStudents = additionalStudents;
      }
      break;
    }

    // AdvertiseActivity / NormalActivity (Open): roster size is not constrained (FLEX-3814),
    // but Activity Cap must not be below non–plus-add registered students (FLEX-4483).
    case ActivityPriorityType.AdvertiseActivity:
    case ActivityPriorityType.NormalActivity: {
      validateOpenOrAdvertisedActivityCap(form);
      result = null;
      break;
    }

    case ActivityPriorityType.TeacherRequested: {
      const maxAttendees = Number(form.controls.maxAttendees.value);
      const rosterStudentsCount = form.controls.rosterStudents.value?.length || 0;
      const excludedStudentsCount = form.controls.excludedStudents.value?.length || 0;
      const additionalStudentsCount = form.controls.additionalStudents.value?.length || 0;
      const teacherRequestedStudentsCount = form.controls.teacherRequestedStudents?.value?.length || 0;
      const registeredStudentsCount = Number(form.controls.registeredStudentsCount?.value) || 0;
      
      const rosterStudents = rosterStudentsCount - excludedStudentsCount;
      const additionalStudents = additionalStudentsCount;
      const explicitCount = rosterStudents + additionalStudents + teacherRequestedStudentsCount;
      const allStudents = registeredStudentsCount > 0 ? registeredStudentsCount : explicitCount;

      if (allStudents > maxAttendees) {
        const errorMessageKey = registeredStudentsCount > 0 
          ? 'REGISTERED_STUDENTS_COUNT_GREATER_THAN_ACTIVITY_CAP'
          : 'STUDENTS_COUNT_GREATER_THAN_ACTIVITY_CAP';
        
        const errorParams = {
          maxAttendees,
          rosterStudents,
          additionalStudents,
          teacherRequestedStudents: teacherRequestedStudentsCount,
          registeredStudents: registeredStudentsCount,
          allStudents,
          totalRegisteredStudents: registeredStudentsCount > 0 ? registeredStudentsCount : allStudents
        };
        
        form.controls.maxAttendees.setErrors({
          validateMaxAttendees: {
            message: {
              text: errorMessageKey,
              interpolateParams: errorParams
            }
          }
        });
        
        result = null;
      } else {
        if (form.controls.maxAttendees.errors?.['validateMaxAttendees']) {
          const errors = { ...form.controls.maxAttendees.errors };
          delete errors['validateMaxAttendees'];
          form.controls.maxAttendees.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
      break;
    }
    default: {
      break;
    }
  }

  if (!message.validateMaxAttendees.message.text) {
    result = null;
  }

  return result;
}

/**
 * Non–plus-add registered count used for Open/Advertised Activity Cap checks (FLEX-4483).
 * Plus-add / teacher-requested students may exceed the cap.
 */
export function getNonPlusAddRegisteredCount(
  registeredStudentsCount?: number | null,
  teacherRequestedStudents?: { length: number } | null
): number {
  const total = Number(registeredStudentsCount) || 0;
  const plusAddCount = teacherRequestedStudents?.length || 0;
  return Math.max(0, total - plusAddCount);
}

/** Required / Admin Required / Priority — registrations drop when converting to choice (FLEX-4499). */
export function isMandatoryActivityPriorityType(type?: ActivityPriorityType | null): boolean {
  return (
    type === ActivityPriorityType.AdminMustAttend ||
    type === ActivityPriorityType.PriorityRoster ||
    type === ActivityPriorityType.RegularRoster
  );
}

/** Optional / Advertised / Open */
export function isChoiceActivityPriorityType(type?: ActivityPriorityType | null): boolean {
  return (
    type === ActivityPriorityType.OptionalRoster ||
    type === ActivityPriorityType.AdvertiseActivity ||
    type === ActivityPriorityType.NormalActivity
  );
}

/**
 * FLEX-4499: Activity Cap vs registered-count validation must not run when registrations
 * are dropped (mandatory → choice). Open ↔ Advertised keeps registrations.
 */
export function doesPriorityChangeDropRegistrations(
  fromType?: ActivityPriorityType | null,
  toType?: ActivityPriorityType | null
): boolean {
  return isMandatoryActivityPriorityType(fromType) && isChoiceActivityPriorityType(toType);
}

/** Clears stale registered count + cap error after a registration-dropping priority change. */
export function clearRegisteredCountAfterPriorityDrop(form?: UntypedFormGroup): void {
  if (!form?.controls.registeredStudentsCount) {
    return;
  }
  form.controls.registeredStudentsCount.setValue(0);
  clearValidateMaxAttendeesControlError(form);
}

function clearValidateMaxAttendeesControlError(form: UntypedFormGroup): void {
  if (!form.controls.maxAttendees.errors?.['validateMaxAttendees']) {
    return;
  }
  const errors = { ...form.controls.maxAttendees.errors };
  delete errors['validateMaxAttendees'];
  form.controls.maxAttendees.setErrors(Object.keys(errors).length > 0 ? errors : null);
}

function validateOpenOrAdvertisedActivityCap(form: UntypedFormGroup): void {
  const maxAttendees = Number(form.controls.maxAttendees.value);
  const registeredStudentsCount = Number(form.controls.registeredStudentsCount?.value) || 0;
  const nonPlusAddRegistered = getNonPlusAddRegisteredCount(
    registeredStudentsCount,
    form.controls.teacherRequestedStudents?.value
  );

  if (registeredStudentsCount > 0 && nonPlusAddRegistered > maxAttendees) {
    const existingErrors = { ...(form.controls.maxAttendees.errors || {}) };
    form.controls.maxAttendees.setErrors({
      ...existingErrors,
      validateMaxAttendees: {
        message: {
          text: 'REGISTERED_STUDENTS_COUNT_GREATER_THAN_ACTIVITY_CAP',
          interpolateParams: {
            totalRegisteredStudents: nonPlusAddRegistered,
            maxAttendees
          }
        }
      }
    });
    return;
  }

  clearValidateMaxAttendeesControlError(form);
}

/**
 * Resets roster selection when the activity priority changes.
 * Shared attendance (shared teachers) is intentionally untouched: it is not part of the roster
 * and must survive a priority change.
 */
export function resetRosterFormControls(form?: UntypedFormGroup): void {
  if (!form) {
    return;
  }

  form.controls.rosterStudents.setValue([]);
  form.controls.additionalStudents.setValue([]);
  form.controls.defaultRoster.setValue(null);
}
